import React, { useState, useEffect, useCallback } from 'react';
import execucaoTecnicaService from '../../services/execucaoTecnica.service';
import ExecucaoTecnicaModal from './ExecucaoTecnicaModal';   // r236
import SePode              from '../../components/SePode';
import BrowseHeader        from '../../components/BrowseHeader';
import { iconeDaRotina }   from '../../constants/iconesRotinas';

// ════════════════════════════════════════════════════════════════════════════
// ExecucaoTecnicaPage.jsx — r221
// ────────────────────────────────────────────────────────────────────────────
// Browse dos projetos que já saíram do Planejamento e estão em andamento
// técnico. Mesmo modelo visual do Planejamento de Projeto (tabela + painel de
// filtros embaixo), com uma diferença:
//
//   • ETAPAS: só Execução, Qualidade, Validação Cliente e Acompanhamento. As
//     etapas 'Planejamento' e 'Encerrado' NÃO aparecem aqui — projeto que
//     entra numa delas some desta tela, o que é o comportamento desejado.
//     Por isso a lista já chega recortada (ETAPAS_VISIVEIS) antes de qualquer
//     filtro, e o contador "X de Y" conta só o que é desta tela.
//
// r235: tipografia de volta ao padrão do portal — Manrope e a mesma escala da
// tela de Planejamento (11/12/13/14). Entre a r221 e a r234a esta tela serviu
// de laboratório pra testar fontes (Barlow, Pathway Extreme, Anek Telugu,
// Yantramanav, Sofia Sans, Golos Text, Monda, Beiruti, Miriam Libre,
// Inclusive Sans, Exo 2, Blinker, IBM Plex Sans e Sintony) com a escala
// ajustada junto; nenhuma vingou. As cores e os pesos definidos nas r222/r228
// (código e nome do projeto em negrito, texto escuro em #404040, "Tipo" e data
// de fechamento na cor do responsável) FICAM — não eram parte do teste.
//
// Os dados vêm de /execucao-tecnica, que roda a mesma consulta de
// /planejamento — o endpoint separado existe pela permissão (rotina própria).
// ════════════════════════════════════════════════════════════════════════════

// r235: Manrope — a mesma do resto do portal, ja carregada no index.html.
//       A constante fica pra tela continuar declarando a familia no container
//       raiz (e nos inline styles que precisam repetir), sem depender de
//       heranca implicita.
const FONTE = "'Manrope', sans-serif";

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', muted: '#888',
  // r231: texto escuro do browse — #404040 no lugar do #1a1a2e do resto do
  //       portal (descricao, tipo, resp. tecnico, data de fechamento, labels).
  texto:   '#404040',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#1D9E75',
  // r222: cinza mais escuro que o COR.muted (#888) — o nome do cliente, embaixo
  //       do projeto, e informacao de leitura, nao rodape.
  cinzaTexto: '#4b5563',
};

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '14px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: FONTE,
};

const TIPOS = [
  { v: 'P', l: 'Projeto'      },
  { v: 'U', l: 'Pontual'      },
  { v: 'S', l: 'Service Desk' },
  { v: 'T', l: 'Sustentação'  },
];

// Etapas desta tela. Mesmas cores do Planejamento, pra um projeto não trocar
// de cor conforme a tela em que é visto.
const ETAPAS_TEC = [
  { v: 'Execução',          l: 'Execução',          cor: '#0ea5e9', bg: '#e0f2fe' },
  { v: 'Qualidade',         l: 'Qualidade',         cor: '#a855f7', bg: '#f3e8ff' },
  { v: 'Validação Cliente', l: 'Validação Cliente', cor: '#f59e0b', bg: '#fef3c7' },
  { v: 'Acompanhamento',    l: 'Acompanhamento',    cor: '#06b6d4', bg: '#cffafe' },
];
const ETAPAS_VISIVEIS = ETAPAS_TEC.map(e => e.v);

// ─── Helpers ────────────────────────────────────────────────────────────────
const labelTipo = (v) => TIPOS.find(t => t.v === v)?.l || v;
const etapaTec  = (v) => ETAPAS_TEC.find(e => e.v === v) || { l: v, cor: '#6b7280', bg: '#f3f4f6' };

const formatarDataBR = (iso) => {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const diasDesde = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const dt = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]), 12, 0, 0);
  const hoje = new Date(); hoje.setHours(12, 0, 0, 0);
  return Math.max(0, Math.round((hoje.getTime() - dt.getTime()) / 86400000));
};

const corDias = (dias) => {
  if (dias === null) return { bg: '#f3f4f6', cor: '#6b7280' };
  if (dias < 7)      return { bg: '#f0fff4', cor: '#1D9E75' };
  if (dias <= 15)    return { bg: '#fffbeb', cor: '#d97706' };
  return                    { bg: '#fff0f0', cor: '#e53935' };
};

// ─── Componentes auxiliares ─────────────────────────────────────────────────
function TagCliente({ tag, cor }) {
  if (!tag) return <span style={{ color: COR.muted, fontSize: '12px' }}>—</span>;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const corTexto = lum > 0.5 ? '#1a1a2e' : '#ffffff';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: bgCor, color: corTexto, letterSpacing: '0.3px',
    }}>{tag}</span>
  );
}

function EtapaTecPill({ etapa }) {
  const e = etapaTec(etapa);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: e.bg, color: e.cor,
      border: `1px solid ${e.cor}`,
    }}>{e.l}</span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
function ExecucaoTecnicaPage() {
  const [lista, setLista]           = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca]           = useState('');
  const [ordenacao, setOrdenacao]   = useState({ campo: 'id', dir: 'asc' });

  // Default: todos os tipos e TODAS as etapas desta tela marcadas — aqui o
  // recorte já é a fase técnica em andamento, não faz sentido abrir vazio.
  const [filtroTipos, setFiltroTipos]         = useState(new Set(TIPOS.map(t => t.v)));
  const [filtroEtapasTec, setFiltroEtapasTec] = useState(new Set(ETAPAS_VISIVEIS));

  // r236: modal de execução (árvore de itens do projeto)
  const [modalAberto, setModalAberto]         = useState(false);
  const [projetoIdAberto, setProjetoIdAberto] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await execucaoTecnicaService.listar();
      // Recorta já na entrada: a tela só existe pras 4 etapas em andamento.
      setLista((dados || []).filter(n => ETAPAS_VISIVEIS.includes(n.etapa_tecnica)));
    } catch (err) {
      console.error('Erro ao carregar execução técnica:', err);
      setLista([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ═══ Filtro + Ordenação ═══
  const listaFiltrada = lista
    .filter(n => {
      const q = busca.toLowerCase();
      const matchBusca = !q
        || (n.id || '').toLowerCase().includes(q)
        || (n.descricao || '').toLowerCase().includes(q)
        || (n.cliente_nome || '').toLowerCase().includes(q)
        || (n.resp_tecnico_nome || '').toLowerCase().includes(q);
      return matchBusca
        && filtroTipos.has(n.tipo)
        && filtroEtapasTec.has(n.etapa_tecnica);
    })
    .sort((a, b) => {
      const va = (a[ordenacao.campo] ?? '').toString().toLowerCase();
      const vb = (b[ordenacao.campo] ?? '').toString().toLowerCase();
      return ordenacao.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

  const toggleOrdem = (campo) =>
    setOrdenacao(o => ({ campo, dir: o.campo === campo && o.dir === 'asc' ? 'desc' : 'asc' }));

  const setaCabecalho = (campo) => {
    if (ordenacao.campo !== campo) return ' ↕';
    return ordenacao.dir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleSet = (setter, valor) => {
    setter(s => { const n = new Set(s); n.has(valor) ? n.delete(valor) : n.add(valor); return n; });
  };
  const toggleAllSet = (set, setter, todos) => {
    setter(set.size === todos.length ? new Set() : new Set(todos));
  };

  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ padding: '6px 20px 20px', fontFamily: FONTE }}>
      <BrowseHeader
        rotina="execucao-tecnica"
        titulo="Execução Técnica"
        descricao="Projetos em execução, qualidade, validação do cliente e acompanhamento."
      />

      {/* ═══════════ BROWSE ═══════════ */}
      <div style={{
        flex: 1, backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        overflow: 'hidden', minWidth: 0, marginBottom: '12px',
      }}>
        {/* Header interno do browse: contagem + busca à direita */}
        <div style={{
          padding: '14px 16px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: COR.muted, flex: 1 }}>
            {listaFiltrada.length} de {lista.length} registro{lista.length !== 1 ? 's' : ''}
          </span>
          <input
            type="text" placeholder="Pesquisar..."
            value={busca} onChange={e => setBusca(e.target.value)}
            style={{ ...inputStyle, width: '220px' }}
          />
        </div>

        {/* Tabela */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: COR.azul }}>
                {/* 1ª coluna fixa pro ícone SVG da rotina */}
                <th style={{ padding: '10px 14px', width: '36px' }}></th>
                {[
                  { label: 'Código',          campo: 'id'                       },
                  { label: 'Descrição',       campo: 'descricao'                },
                  { label: 'Cliente',         campo: 'cliente_tag'              },
                  { label: 'Tipo',            campo: 'tipo'                     },
                  { label: 'Etapa',           campo: 'etapa_tecnica'            },
                  { label: 'Resp. técnico',   campo: 'resp_tecnico_nome'        },
                  { label: 'Data fechamento', campo: 'dt_fechamento'            },
                  { label: 'Dias etapa',      campo: 'dt_entrada_etapa_tecnica' },
                ].map(col => (
                  <th key={col.campo}
                    onClick={() => toggleOrdem(col.campo)}
                    style={{
                      padding: '10px 14px', textAlign: 'left',
                      color: COR.branco, fontWeight: '700',
                      fontSize: '13px',
                      cursor: 'pointer', userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >{col.label}{setaCabecalho(col.campo)}</th>
                ))}
                <th style={{
                  padding: '10px 14px', textAlign: 'left',
                  color: COR.branco, fontWeight: '700', fontSize: '13px',
                }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: COR.muted, fontSize: '13px' }}>
                    Carregando...
                  </td>
                </tr>
              ) : listaFiltrada.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: COR.muted, fontSize: '13px' }}>
                    Nenhum projeto em execução técnica com os filtros atuais.
                  </td>
                </tr>
              ) : (
                listaFiltrada.map(n => {
                  const dias  = diasDesde(n.dt_entrada_etapa_tecnica);
                  const cores = corDias(dias);
                  return (
                    <tr key={n.id} style={{ borderBottom: `1px solid ${COR.borda}` }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.cinzaBg}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = COR.branco}
                    >
                      <td style={{ padding: '10px 0 10px 14px', verticalAlign: 'middle' }}>
                        {iconeDaRotina('execucao-tecnica', { width: 20, height: 20, color: COR.azul })}
                      </td>
                      {/* r222: codigo do projeto em negrito */}
                      <td style={{ padding: '10px 14px', fontWeight: '700', color: COR.azul, whiteSpace: 'nowrap' }}>
                        {n.id}
                      </td>
                      <td style={{ padding: '10px 14px', color: COR.texto, maxWidth: '320px' }}>
                        {/* r222: nome do projeto em negrito e 1pt acima do corpo
                            da tabela — e o dado que se procura na varredura. */}
                        <div style={{
                          fontSize: '14px', fontWeight: '700',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {n.descricao}
                        </div>
                        {/* r222: cliente maior (12 -> 13px) e num cinza mais escuro */}
                        <div style={{ fontSize: '12px', color: COR.cinzaTexto, marginTop: '2px' }}>
                          {n.cliente_razao_social || n.cliente_nome}
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <TagCliente tag={n.cliente_tag} cor={n.cliente_cor_tag} />
                      </td>
                      {/* r228: mesma cor do responsavel tecnico (COR.texto) —
                          era COR.muted, que deixava o tipo apagado demais. */}
                      <td style={{ padding: '10px 14px', color: COR.texto, fontSize: '12px' }}>
                        {labelTipo(n.tipo)}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <EtapaTecPill etapa={n.etapa_tecnica} />
                      </td>
                      <td style={{ padding: '10px 14px', color: COR.texto, fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {n.resp_tecnico_nome || <span style={{ color: COR.muted, fontStyle: 'italic' }}>sem responsável</span>}
                      </td>
                      {/* r228a: mesma cor do responsavel tecnico e do tipo
                          (COR.texto) — era COR.muted. */}
                      <td style={{ padding: '10px 14px', color: COR.texto, fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {formatarDataBR(n.dt_fechamento)}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span
                          title={dias === null
                            ? 'Sem registro de entrada'
                            : `${dias} dia${dias === 1 ? '' : 's'} desde a entrada na etapa`}
                          style={{
                            display: 'inline-block',
                            padding: '2px 10px', borderRadius: '20px',
                            fontSize: '12px', fontWeight: '500',
                            backgroundColor: cores.bg, color: cores.cor,
                            border: `1px solid ${cores.cor}`,
                            minWidth: '32px', textAlign: 'center',
                          }}>
                          {dias === null ? '—' : `${dias}d`}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                        <SePode rotina="execucao-tecnica" acao="alterar">
                          <button
                            type="button"
                            onClick={() => {
                              setProjetoIdAberto(n.id);
                              setModalAberto(true);
                            }}
                            title="Abrir a execução do projeto"
                            style={{
                              padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                              border: `1px solid ${COR.azul}`,
                              backgroundColor: COR.branco, color: COR.azul,
                              borderRadius: '5px', cursor: 'pointer',
                              whiteSpace: 'nowrap', fontFamily: FONTE,
                            }}
                          >Executar</button>
                        </SePode>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════ PAINEL DE FILTROS ═══════════ */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        padding: '14px 16px', display: 'flex', gap: '16px', flexWrap: 'wrap',
      }}>
        {/* ── Filtro Tipo — 2 col × 2 linhas ── */}
        <div style={{
          minWidth: '180px',
          border: `1px solid ${COR.borda}`, borderRadius: '6px',
          padding: '10px 12px', backgroundColor: COR.cinzaBg,
        }}>
          <div
            onClick={() => toggleAllSet(filtroTipos, setFiltroTipos, TIPOS.map(t => t.v))}
            title="Clique para marcar/desmarcar todos"
            style={{
              fontSize: '11px', fontWeight: '700', color: COR.azul,
              textTransform: 'uppercase', letterSpacing: '0.5px',
              marginBottom: '8px', cursor: 'pointer', userSelect: 'none',
            }}
          >Tipo</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: '12px', rowGap: '4px',
          }}>
            {TIPOS.map(t => (
              <label key={t.v} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '12px', color: COR.texto,
                cursor: 'pointer', userSelect: 'none',
              }}>
                <input type="checkbox" checked={filtroTipos.has(t.v)}
                  onChange={() => toggleSet(setFiltroTipos, t.v)}
                  style={{ cursor: 'pointer' }} />
                {t.l}
              </label>
            ))}
          </div>
        </div>

        {/* ── Filtro Etapa Técnica — 2 col × 2 linhas (só as 4 desta tela) ── */}
        <div style={{
          minWidth: '300px',
          border: `1px solid ${COR.borda}`, borderRadius: '6px',
          padding: '10px 12px', backgroundColor: COR.cinzaBg,
        }}>
          <div
            onClick={() => toggleAllSet(filtroEtapasTec, setFiltroEtapasTec, ETAPAS_VISIVEIS)}
            title="Clique para marcar/desmarcar todos"
            style={{
              fontSize: '11px', fontWeight: '700', color: COR.azul,
              textTransform: 'uppercase', letterSpacing: '0.5px',
              marginBottom: '8px', cursor: 'pointer', userSelect: 'none',
            }}
          >Etapa Técnica</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: '12px', rowGap: '4px',
          }}>
            {ETAPAS_TEC.map(e => (
              <label key={e.v} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '12px', color: COR.texto,
                cursor: 'pointer', userSelect: 'none',
              }}>
                <input type="checkbox" checked={filtroEtapasTec.has(e.v)}
                  onChange={() => toggleSet(setFiltroEtapasTec, e.v)}
                  style={{ cursor: 'pointer' }} />
                <span style={{
                  display: 'inline-block', width: '8px', height: '8px',
                  borderRadius: '50%', backgroundColor: e.cor,
                }}></span>
                {e.l}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* r236: execução do projeto — árvore de itens (tópico → atividades) */}
      <ExecucaoTecnicaModal
        aberto={modalAberto}
        projetoId={projetoIdAberto}
        onFechar={async () => {
          setModalAberto(false);
          setProjetoIdAberto(null);
          // Recarrega: a etapa técnica pode ter mudado e o projeto sair da tela
          await carregar();
        }}
        // Gancho das funcionalidades por atividade (a definir). Por enquanto só
        // avisa, pra deixar claro que o botão está ligado e ainda sem destino.
        onAcaoAtividade={(atividade) => {
          window.alert(
            `Ações da atividade ${atividade.codigo}

`
            + 'Funcionalidade ainda não implementada.'
          );
        }}
      />
    </div>
  );
}

export default ExecucaoTecnicaPage;
